// SAM-1095 — CMS › App (mock C8): how the Home screen is put together, with the live Home on a fixed rail.
// Same data and endpoints as the old HomeScreen page (home_config); layout and preview follow the canvas.
// Options the mock does not draw (max items, logo, Wallet logo source, weather colours, focal point, estate group preview)
// stay under "More options" so nothing that worked is lost.
import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { TableContent } from '../LayoutPage';
import {
  useHomeConfig, useUpdateHomeConfig, useCreateHomeConfig, useEstateGroupsForHome, type HomeConfig,
} from '../../hooks/useHomeConfig';
import { useAds } from '../../hooks/useAds';
import { useArticles } from '../../hooks/useArticles';
import { useFdCan } from '../../hooks/useFdCan';
import { useWhiteBorderCheck } from '../../hooks/useWhiteBorderCheck';
import { S3ImageUpload } from '../../components/S3ImageUpload';
import { ColorPicker } from '../../components/ColorPicker';
import { HomePreview, PhoneFrame } from '../../components/cms/AppPreviews';
import { BackgroundFocalEditor, type BackgroundPatch } from '../../components/cms/BackgroundFocalEditor';
import { Card, Field, Segmented, btnPrimary, inputClass, primaryStyle, selectClass } from '../../components/cms/EditorParts';
import { cmsState, dayOf, todayInBusinessClock } from '../../utils/cmsState';

type Form = Omit<HomeConfig, 'id'>;

export const DEFAULT_HOME_CONFIG: Form = {
  name: 'Default Home',
  background_img_url: '',
  image_settings: { background: { fit: 'cover', focal_x: 50, focal_y: 50 } },
  logo_url: '',
  weather_icon_color: '#FFFFFF',
  weather_bg_color: '#333333',
  featured_enabled: true,
  featured_title_en: 'Featured',
  featured_title_es: 'Destacados',
  featured_max_items: 5,
  weekly_enabled: true,
  weekly_title_en: 'Weekly',
  weekly_title_es: 'Semanal',
  weekly_max_items: 5,
  weekly_article_type_id: null,
  is_active: true,
  wallet_logo_source: 'home_config',
};

const Switch: React.FC<{ label: string; on: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ label, on, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className="inline-flex h-[22px] w-10 shrink-0 items-center rounded-full border-0 p-0.5 disabled:opacity-60"
    style={{ background: on ? 'var(--tenant-primary, #0B5C4D)' : '#D1D5DB', justifyContent: on ? 'flex-end' : 'flex-start' }}
  >
    <span className="h-[18px] w-[18px] rounded-full bg-white" />
  </button>
);

const SectionCard: React.FC<{
  title: string; subtitle: string; enabled: boolean; onEnabled: (v: boolean) => void; disabled: boolean;
  titleEn: string; titleEs: string; onTitleEn: (v: string) => void; onTitleEs: (v: string) => void;
  maxItems: number; onMaxItems: (n: number) => void; idPrefix: string;
}> = (p) => (
  <section className="flex min-w-0 flex-col gap-3.5 rounded-lg bg-white p-5 shadow-sm">
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <h2 className="m-0 text-base font-semibold">{p.title}</h2>
        <div className="mt-0.5 text-[13px] text-gray-500">{p.subtitle}</div>
      </div>
      <Switch label={`${p.title} on`} on={p.enabled} onChange={p.onEnabled} disabled={p.disabled} />
    </div>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label="Title · English" htmlFor={`${p.idPrefix}-en`}>
        <input id={`${p.idPrefix}-en`} className={inputClass} value={p.titleEn} disabled={p.disabled} onChange={(e) => p.onTitleEn(e.target.value)} />
      </Field>
      <Field label="Title · Español" htmlFor={`${p.idPrefix}-es`}>
        <input id={`${p.idPrefix}-es`} className={inputClass} value={p.titleEs} disabled={p.disabled} onChange={(e) => p.onTitleEs(e.target.value)} />
      </Field>
    </div>
    <div className="max-w-[200px]">
      <Field label="Max items" htmlFor={`${p.idPrefix}-max`}>
        <input id={`${p.idPrefix}-max`} type="number" min={1} max={20} className={inputClass} value={p.maxItems} disabled={p.disabled} onChange={(e) => p.onMaxItems(parseInt(e.target.value, 10) || 1)} />
      </Field>
    </div>
  </section>
);

export const AppHomePage: React.FC = () => {
  // The tenant Front Desk views the home screen but does not change it (specs/front-desk-tenant-permisos.md).
  const canEdit = useFdCan('cms', 'edit');
  const { config, isLoading } = useHomeConfig();
  const updateConfig = useUpdateHomeConfig();
  const createConfig = useCreateHomeConfig();
  const { estateGroups } = useEstateGroupsForHome();
  const { ads } = useAds();
  const { articles } = useArticles();
  const whiteBorder = useWhiteBorderCheck();

  const [form, setForm] = useState<Form>(DEFAULT_HOME_CONFIG);
  const [lang, setLang] = useState<'en' | 'es'>('en');
  const [estateGroupId, setEstateGroupId] = useState('');

  useEffect(() => {
    if (config) {
      const { id: _id, ...rest } = config;
      setForm(rest);
    }
  }, [config]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const patchBackground = (patch: BackgroundPatch) =>
    setForm((f) => ({
      ...f,
      image_settings: { ...(f.image_settings ?? {}), background: { fit: 'cover', focal_x: 50, focal_y: 50, ...(f.image_settings?.background ?? {}), ...patch } },
    }));

  const today = todayInBusinessClock();
  // What residents get today: published ads inside their dates, by priority; the newest published article.
  const liveAds = useMemo(
    () => ads
      .filter((a) => cmsState(a, today) === 'published')
      .sort((a, b) => (a.priority || 9e9) - (b.priority || 9e9) || (a.id ?? 0) - (b.id ?? 0))
      .slice(0, form.featured_max_items),
    [ads, today, form.featured_max_items],
  );
  const liveArticles = useMemo(
    () => articles
      .filter((a) => cmsState(a, today) === 'published')
      .sort((a, b) => (dayOf(b.publish_date) || '').localeCompare(dayOf(a.publish_date) || '') || (b.id ?? 0) - (a.id ?? 0))
      .slice(0, form.weekly_max_items),
    [articles, today, form.weekly_max_items],
  );

  const group = estateGroups.find((g) => String(g.id) === estateGroupId);
  const bg = form.image_settings?.background;
  const heroKey = group?.home_url || form.background_img_url;
  const saving = updateConfig.isPending || createConfig.isPending;
  const save = () => { if (config?.id) updateConfig.mutate({ id: config.id, data: form }); };

  if (isLoading) return <TableContent><div className="p-8 text-sm text-gray-500">Loading…</div></TableContent>;

  if (!config) {
    return (
      <TableContent>
        <div className="flex flex-col items-center gap-4 p-20 text-center text-gray-500">
          <p className="m-0 font-medium">No home screen configuration found</p>
          <p className="m-0 text-sm">Create a default configuration to get started</p>
          {canEdit && (
            <button type="button" className={btnPrimary} style={primaryStyle} disabled={saving} onClick={() => createConfig.mutate(form)}>
              <Plus size={16} aria-hidden /> {saving ? 'Creating…' : 'Create config'}
            </button>
          )}
        </div>
      </TableContent>
    );
  }

  const featuredTitle = (lang === 'en' ? form.featured_title_en : form.featured_title_es) || (lang === 'en' ? 'Featured' : 'Destacados');
  const weeklyTitle = (lang === 'en' ? form.weekly_title_en : form.weekly_title_es) || (lang === 'en' ? 'Weekly' : 'Semanal');
  const first = liveArticles[0];

  return (
    <div className="flex h-full flex-col bg-gray-50 text-gray-900">
      <div className="px-8 pt-5">
        <div className="text-xs font-semibold text-gray-500">CMS</div>
        <div className="flex items-end justify-between">
          <div>
            <h1 className="m-0 text-[26px] font-bold">App</h1>
            <div className="text-sm text-gray-500">How the Home screen is put together</div>
          </div>
          {canEdit && (
            <button type="button" className={btnPrimary} style={primaryStyle} disabled={saving} onClick={save}>
              {updateConfig.isPending ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex min-h-full items-stretch">
        <fieldset disabled={!canEdit} className="m-0 flex min-w-0 flex-1 flex-col gap-4 border-0 px-8 pb-12 pt-6">
          <SectionCard
            idPrefix="home-featured" title="Featured section" subtitle="The ad carousel at the top. Hides itself when there are no ads in the app."
            enabled={form.featured_enabled} onEnabled={(v) => set('featured_enabled', v)} disabled={!canEdit}
            titleEn={form.featured_title_en} titleEs={form.featured_title_es}
            onTitleEn={(v) => set('featured_title_en', v)} onTitleEs={(v) => set('featured_title_es', v)}
            maxItems={form.featured_max_items} onMaxItems={(n) => set('featured_max_items', n)}
          />
          <SectionCard
            idPrefix="home-weekly" title="Weekly section" subtitle="The articles below. Hides itself when there are no articles in the app."
            enabled={form.weekly_enabled} onEnabled={(v) => set('weekly_enabled', v)} disabled={!canEdit}
            titleEn={form.weekly_title_en} titleEs={form.weekly_title_es}
            onTitleEn={(v) => set('weekly_title_en', v)} onTitleEs={(v) => set('weekly_title_es', v)}
            maxItems={form.weekly_max_items} onMaxItems={(n) => set('weekly_max_items', n)}
          />

          <Card title="Background" subtitle="Photo behind the top of the Home">
            <S3ImageUpload label="" value={form.background_img_url} onChange={(k) => set('background_img_url', k)} onFileChosen={whiteBorder.onFileChosen} previewHeight="112px" objectFit="cover" />
            {whiteBorder.warn && (
              <div role="status" className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                This image has a white or almost white border. It looks washed out in the app: crop the border or pick another photo.
              </div>
            )}
            {form.background_img_url && (
              <details>
                <summary className="cursor-pointer text-[13px] font-semibold text-gray-700">Fit and focal point</summary>
                <div className="pt-3">
                  <BackgroundFocalEditor
                    imageKey={form.background_img_url}
                    fit={bg?.fit ?? 'cover'}
                    focalX={bg?.focal_x ?? 50}
                    focalY={bg?.focal_y ?? 50}
                    onChange={patchBackground}
                    disabled={!canEdit}
                  />
                </div>
              </details>
            )}
          </Card>

          <Card title="More options" subtitle="Logo, Wallet pass and weather pills">
            <Field label="Logo image">
              <S3ImageUpload label="" value={form.logo_url} onChange={(k) => set('logo_url', k)} previewHeight="80px" objectFit="contain" />
            </Field>
            <Field label="Wallet pass logo source" htmlFor="home-wallet-logo" hint="Controls which logo appears on the Apple Wallet pass. Strip/background always uses the home image above.">
              <select id="home-wallet-logo" className={selectClass} value={form.wallet_logo_source} onChange={(e) => set('wallet_logo_source', e.target.value as 'home_config' | 'tenant')}>
                <option value="home_config">Use this home logo</option>
                <option value="tenant">Use tenant settings logo</option>
              </select>
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ColorPicker label="Weather icon color" value={form.weather_icon_color} onChange={(c) => set('weather_icon_color', c)} />
              <ColorPicker label="Weather background color" value={form.weather_bg_color} onChange={(c) => set('weather_bg_color', c)} />
            </div>
          </Card>
        </fieldset>

        <aside className="flex w-[380px] shrink-0 flex-col items-center gap-3.5 border-l border-gray-200 bg-gray-100 px-4 py-6" aria-label="Preview">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Preview · live home</div>
          <Segmented label="Language" value={lang} options={[{ id: 'en', text: 'English' }, { id: 'es', text: 'Español' }]} onChange={(v) => setLang(v as 'en' | 'es')} />
          <PhoneFrame height={700} label="Live Home preview">
            <HomePreview
              bg={{ imageKey: heroKey, fit: bg?.fit ?? 'cover', focalX: bg?.focal_x ?? 50, focalY: bg?.focal_y ?? 50 }}
              featured={{
                enabled: form.featured_enabled,
                title: featuredTitle,
                ads: liveAds.map((a) => ({ title: (lang === 'en' ? a.title_en : a.title_es) || a.title_en, text: (lang === 'en' ? a.short_description_en : a.short_description_es) || '', color: a.background_color, imageKey: a.art_img_url })),
              }}
              weekly={{
                enabled: form.weekly_enabled,
                title: weeklyTitle,
                article: first ? { title: (lang === 'en' ? first.title_en : first.title_es) || first.title_en, imageKey: first.art_img_url } : null,
                count: liveArticles.length,
              }}
              readAll={lang === 'en' ? 'Read all' : 'Ver todo'}
              viewAll={lang === 'en' ? 'View all' : 'Ver todo'}
            />
          </PhoneFrame>
          <Field label="Preview with an estate group's background" htmlFor="home-estate-group">
            <select id="home-estate-group" className={selectClass} value={estateGroupId} onChange={(e) => setEstateGroupId(e.target.value)}>
              <option value="">Use this background</option>
              {estateGroups.map((g) => <option key={g.id} value={String(g.id)}>{g.name}</option>)}
            </select>
          </Field>
        </aside>
      </div>
      </div>
    </div>
  );
};
